<template>
	<div :class="{ 'brand-panel': true, mini: mini, full: !mini, 'full-menu': fullMenu, disabled: disabled }">
		<div class="overlay" v-if="disabled && showFeatureButton">
			<span>
				<h2>Have a collab idea?</h2>
				<span class="button flip-colours mini" @click="featureBrand">Create an opportunity</span>
				<span class="how-it-works" @click="howItWorks">How does the Opportunities area work?</span>
			</span>
		</div>

		<div class="overlay" v-if="disabled && pendingApproval && !requiresPayment">
			<span>
				<h2>{{ pendingApprovalTitle }}</h2>
				<p>
					You'll receive an email from us once your Opportunity has been approved (normally within 24 hours). If there are any problems
					we'll be in touch with further details.
				</p>
				<span class="button flip-colours mini" @click="editFeature">Edit your opportunity</span>
			</span>
		</div>

		<div class="overlay" v-if="disabled && pendingApproval && requiresPayment">
			<span>
				<h2>Payment required</h2>
				<p>Please click the button below to pay for your opportunity, it will then be sent for approval.</p>
				<span class="button mini" @click="editFeature">Edit your opportunity</span> &nbsp;
				<span class="button flip-colours mini" @click="payForFeature">Pay now</span>
			</span>
		</div>

		<div class="brand-info">
			<div class="images-wrap">
				<div class="lifestyle-image">
					<img :src="lifestyleImageSrc" v-if="mini && lifestyleImageSrc" @click="clicked" class="clickable" />
					<img :src="lifestyleImageSrc" v-if="!mini && lifestyleImageSrc" />
				</div>

				<div class="logo-wrap">
					<div class="logo" :style="logoContainerStyles">
						<img :src="brandLogoSrc" v-if="!mini && brandLogoSrc" :style="logoStyles" />
						<img :src="brandLogoSrc" v-else-if="brandLogoSrc" @click="clicked" class="clickable" :style="logoStyles" />
					</div>
				</div>
				<div class="category-labels" v-if="categoryLabels.length">
					<span class="category-label" :class="{ 'category-label--extra': index > 0 }" v-for="(label, index) in categoryLabels" :key="label">{{
						label
					}}</span>
				</div>
			</div>

			<h2 class="brand-name" v-html="brandInfo.brand_name" v-if="!brandInfo.brand_website" />

			<h2 class="brand-name" v-if="brandInfo.brand_website">
				<a :href="brandInfo.brand_website" target="_blank" v-if="brandInfo.brand_website" v-html="brandInfo.brand_name" />
			</h2>

			<div v-if="brandInfo.brand_website" class="brand-website">
				<Icon type="website" class="svg" colour="rgba(0, 0, 0, 0.56)" />
				<a :href="brandInfo.brand_website" target="_blank" v-if="brandInfo.brand_website" v-html="brandInfo.brand_website_label" />
			</div>

			<div v-if="canViewBrandDetails" class="brand-details-link" @click.stop="viewBrandDetails">
				<Icon type="id" class="svg" colour="rgba(0, 0, 0, 0.56)" />
				<span>View their brand details</span>
			</div>

			<div v-if="brandInfo.featureOnly && !hasActiveSubscription" class="opp-type">
				<Icon type="star" class="svg" colour="#679DA2" />
				<span class="paid-opp">Free to pitch! (no subscription required)</span>
			</div>

			<span
				ref="mobilePitchButton"
				class="button flip-colours mini no-margin pitch-your-brand-mobile"
				v-if="!hasAlreadyPitched"
				@click="scrollToForm"
				>Send your pitch</span
			>

			<div class="already-pitched already-pitched-mini" v-if="hasAlreadyPitched && (!mini || !hideButton)">
				<Icon type="envelope-open-text" class="svg" colour="#679DA2" />
				<span>You pitched to this opportunity on the {{ pitchedOnLabel }}</span>
			</div>

			<div class="collab-benefits" v-if="benefits.length">
				<h3 class="benefits-title">What's in it for your brand?</h3>
				<ul class="benefits-list">
					<li v-for="(b, idx) in benefits" :key="idx">{{ b }}</li>
				</ul>
			</div>

			<hr />

			<h3 class="collab-description" v-html="data.pitch_title" v-if="data.pitch_title" />

			<div class="pitch-description">
				<p ref="description" :class="{ truncated: mini && isTruncated && !isExpanded }" v-html="displayedDescription"></p>
				<span v-if="mini && isTruncated" class="read-more" @click="toggleReadMore">{{ isExpanded ? "Read less" : "Read more" }}</span>
			</div>

			<RequirementsList :items="data.lists.wants" v-if="data.lists && data.lists.wants" />

			<RequirementsList :items="data.lists.bads" :positive="false" v-if="data.lists && data.lists.bads" />

			<div>
				<span
					v-if="mini && !featureOnly && !hideButton && !hasAlreadyPitched"
					:class="{ 'button flip-colours pitch-button': true, 'full-menu': fullMenu, disabled: type === 'feature' }"
					@click="pitchToCollab"
					>Pitch to collab</span
				>

				<span class="pitch-btn-label" v-if="mini && !featureOnly && !hideButton && brandInfo.featureOnly && !hasActiveSubscription && !hasAlreadyPitched"
					>Free to pitch! (no subscription required)</span
				>

				<span class="pitch-btn-label" v-if="hasAlreadyPitched && !hideButton"
					>You pitched to this opportunity on the {{ pitchedOnLabel }}</span
				>

				<div v-if="showMatchLink" class="match-link-notice" @click.stop>
					Not interested in this opportunity but interested in this brand? <br />They are in your matches,
					<a href="#" @click.prevent="viewBrandDetailsWithButtons">click here to send them a collab request</a>.
				</div>
			</div>
		</div>

		<slot name="pitch-form"></slot>
	</div>
</template>

<script>
import { ref, computed, watch, onMounted, nextTick, getCurrentInstance } from "vue";
import RequirementsList from "./RequirementsList.vue";
import Icon from "./BaseIcon.vue";

export default {
	name: "BrandPanel",
	components: { RequirementsList, Icon },
	props: {
		mini: {
			type: Boolean,
			default: true,
		},
		data: {
			type: Object,
			default: () => ({}),
		},
		type: {
			type: String,
			default: "pitch",
		},
		hideButton: {
			type: Boolean,
			default: false,
		},
		autoScrollToPitchFormOnMount: {
			type: Boolean,
			default: false,
		},
	},
	setup(props, { emit }) {
		const vm = getCurrentInstance().proxy;
		const store = vm.$store;
		const description = ref(null);
		const mobilePitchButton = ref(null);

		const linesToTruncate = ref(30);
		const isExpanded = ref(false);
		const isTruncated = ref(false);

		const fullMenu = computed(() => store.state.general.fullMenu);
		const featureOnly = computed(() => store.state.general.featureOnly);
		const brand = computed(() => store.state.general.brand);
		const userBrands = computed(() => store.state.general.userBrands);
		const hasActiveSubscription = computed(() => !!store.state.general.subscription?.active);

		const panelWidth = computed(() => (props.mini ? "86.5vw" : "100%"));

		const brandInfo = computed(() => {
			if (props.type === "feature") {
				if (!userBrands.value) return {};
				let b = userBrands.value.find((b) => b.id === brand.value);
				if (b) {
					return {
						brand_name: b.label,
						brand_website: b.website,
						brand_website_label: b.website,
					};
				}
				return {};
			}
			return props.data;
		});

		const disabled = computed(() => !!props.data.disabled);
		const requiresPayment = computed(() => !!props.data.requiresPayment);
		const showFeatureButton = computed(() => !!props.data.showFeatureButton);
		const pendingApproval = computed(() => !!props.data.pendingApproval);
		const freeOpportunity = computed(() => !!props.data.freeOpportunity);
		const pendingApprovalTitle = computed(() => {
			if (!featureOnly.value || freeOpportunity.value) return "Your opportunity has been sent for approval";
			return "Thanks for your payment";
		});

		const benefits = computed(() => {
			if (!props.data) return [];
			const list = Array.isArray(props.data.collab_benefits)
				? props.data.collab_benefits
				: props.data.collab_benefits
					? [props.data.collab_benefits]
					: [];

			return list.map((benefit) => (typeof benefit === "string" ? benefit.trim() : "")).filter((benefit) => benefit.length > 0);
		});

		const hasAlreadyPitched = computed(() => !!props.data && !!props.data.alreadyPitched);
		const pitchedOnLabel = computed(() => {
			if (!props.data || !props.data.pitched_on_label) return "this date";
			return props.data.pitched_on_label;
		});

		const normalizeLabel = (label) => (typeof label === "string" ? label.trim() : "");

		const categoryLabels = computed(() => {
			if (!props.data) return [];

			const labels = [normalizeLabel(props.data.category_label)];
			const extraLabels = Array.isArray(props.data.extra_labels)
				? props.data.extra_labels
				: props.data.extra_labels
					? String(props.data.extra_labels).split("|")
					: [];

			extraLabels.forEach((label) => {
				labels.push(normalizeLabel(label));
			});

			return [...new Set(labels.filter((label) => label.length > 0))];
		});

		const normalizedLogoBgColour = computed(() => {
			const value = props.data?.brand_logo_bg_colour;
			if (typeof value !== "string") return "";
			const colour = value.trim();
			return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(colour) ? colour : "";
		});

		const logoContainerStyles = computed(() => {
			return normalizedLogoBgColour.value ? { backgroundColor: normalizedLogoBgColour.value } : {};
		});

		const cacheBustedImageSrc = (value) => {
			if (typeof value !== "string") return "";
			const src = value.trim();
			if (!src) return "";
			return `${src}${src.includes("?") ? "&" : "?"}a=${props.data?.approved ? 3 : "0"}`;
		};

		const lifestyleImageSrc = computed(() => cacheBustedImageSrc(props.data?.["pitch-lifestyle"]));
		const brandLogoSrc = computed(() => cacheBustedImageSrc(props.data?.["brand-logo"]));

		const displayedDescription = computed(() => {
			if (!props.data || !props.data.pitch_description) return "";
			let d = props.data.pitch_description.replace(/\n/g, "<br />");
			if (isExpanded.value || !isTruncated.value) {
				return d;
			}
			return (
				d
					.split(" ")
					.slice(0, linesToTruncate.value * 15)
					.join(" ") + "..."
			);
		});

		const clicked = () => {
			if (props.type === "feature") return;
			emit("pitch", props.data.id);
		};

		const logoStyles = computed(() => {
			let s = {};
			if (props.data.image_max_width_percent) {
				s.maxWidth = props.data.image_max_width_percent + '%';
			}
			return s;
		});

		const isVisibleByCss = (el) => {
			if (!el) return false;

			const styles = window.getComputedStyle(el);
			if (styles.display === "none" || styles.visibility === "hidden" || Number(styles.opacity) === 0) return false;

			const rect = el.getBoundingClientRect();
			return rect.width > 0 && rect.height > 0;
		};

		const scrollToFormIfMobileButtonVisible = () => {
			if (isVisibleByCss(mobilePitchButton.value)) {
				scrollToForm();
			}
		};

		const pitchToCollab = () => {
			if (props.type === "feature") return;
			emit("pitch", props.data.id, { scrollToForm: true });
		};

		const featureBrand = () => {
			emit("feature");
		};

		const editFeature = () => {
			emit("edit-feature", props.data.id);
		};

		const howItWorks = () => {
			emit("how-it-works");
		};

		const toggleReadMore = () => {
			isExpanded.value = !isExpanded.value;
		};

		const checkTruncation = () => {
			if (description.value) {
				const lineHeight = parseInt(window.getComputedStyle(description.value).lineHeight);
				isTruncated.value = description.value.scrollHeight > lineHeight * linesToTruncate.value;
			}
		};

		const scrollToForm = () => {
			emit("scroll-to-pitch-form");
		};

		const payForFeature = () => {
			emit("pay-for-feature", props.data.id);
		};

		const canViewBrandDetails = computed(() => {
			// Only show on actual opportunity cards (not feature previews/edits)
			// that were NOT created by feature-only brands
			return (
				props.type !== "feature" && props.data && props.data.id && props.data.brand_id && !props.data.dummy && !brandInfo.value.featureOnly
			);
		});

		const viewBrandDetails = async () => {
			try {
				await store.dispatch("general/openOpportunityBrandCardPopup", {
					brandId: props.data.brand_id,
					pitchId: props.data.id,
				});
			} catch (err) {
				console.error("Error opening brand details:", err);
			}
		};

		const showMatchLink = computed(() => {
			return canViewBrandDetails.value && props.data && props.data.brandInMatches && !props.data.thisUser && !hasAlreadyPitched.value;
		});

		const viewBrandDetailsWithButtons = async () => {
			try {
				await store.dispatch("general/openOpportunityBrandCardPopupWithButtons", {
					brandId: props.data.brand_id,
					pitchId: props.data.id,
				});
			} catch (err) {
				console.error("Error opening brand details:", err);
			}
		};

		onMounted(() => {
			nextTick(() => {
				checkTruncation();
				if (props.autoScrollToPitchFormOnMount) {
					requestAnimationFrame(scrollToFormIfMobileButtonVisible);
				}
			});
		});

		watch(
			() => props.data.pitch_description,
			() => {
				nextTick(checkTruncation);
			}
		);

		return {
			description,
			mobilePitchButton,
			fullMenu,
			featureOnly,
			hasActiveSubscription,
			panelWidth,
			brandInfo,
			disabled,
			requiresPayment,
			showFeatureButton,
			pendingApproval,
			pendingApprovalTitle,
			benefits,
			hasAlreadyPitched,
			pitchedOnLabel,
			categoryLabels,
			logoContainerStyles,
			lifestyleImageSrc,
			brandLogoSrc,
			displayedDescription,
			isTruncated,
			isExpanded,
			linesToTruncate,
			clicked,
			pitchToCollab,
			featureBrand,
			editFeature,
			howItWorks,
			toggleReadMore,
			checkTruncation,
			scrollToForm,
			payForFeature,
			canViewBrandDetails,
			viewBrandDetails,
			showMatchLink,
			viewBrandDetailsWithButtons,
			logoStyles
		};
	},
};
</script>

<style lang="less" scoped>
@import "./../../assets/css/less/_settings.less";
@import "./../../assets/css/less/_helper.less";
.clickable {
	cursor: pointer;
}
.brand-panel {
	.rounded(22px);
	padding: 0 0 20px;
	background: @surfacePanel;
	overflow: hidden;
	--logo-box-width: 180px;
	--logo-box-height: 96px;

	.brand-info {
		position: relative;
		padding: 0 24px;

		.category-labels {
			position: absolute;
			right: 10px;
			top: 10px;
			z-index: 1;
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			gap: 5px;
			width: auto !important;
		}

		.category-label {
			border: 1px solid rgba(0, 0, 0, 0.56);
			background: @surfacePanel;
			color: @textBody;
			padding: 0 6px;
			font-size: 0.75rem;
			border-radius: @radiusXs;
			width: auto !important;
		}

		.category-label--extra {
			border-color: @brown;
			color: @brown;
		}

		.pitch-btn-label {
			display: inline-block;
			padding: 1em 0;
			font-size: 0.8em;
			color: @textSubtle;
		}

		.brand-website,
		.brand-details-link,
		.opp-type {
			display: flex;
			flex-direction: row;
			align-items: center;
			margin: 8px 0 0;
			color: @textSubtle;

			.svg {
				width: 19px;
				height: 19px;
			}

			a,
			span {
				color: inherit;
				display: inline-block;
				margin-left: 10px;
				font-size: 1rem;
				text-decoration: underline;
				text-decoration-thickness: 1px;
				text-underline-offset: 2px;
			}
		}

		.brand-details-link {
			cursor: pointer;
			&:hover {
				color: @textBody;
			}
		}

		.match-link-notice {
			margin: 15px 0 0;
			font-size: 0.8125rem;
			color: @textSubtle;
			line-height: 1.4;

			a {
				color: @textBody;
				text-decoration: underline;
				text-decoration-thickness: 1px;
				text-underline-offset: 2px;

				&:hover {
					color: @textStrong;
				}
			}
		}

		.opp-type {
			color: @formDefaultColour;

			span {
				font-size: 0.875rem;
				font-weight: @bold;
				text-decoration: none;
			}
		}
		.mutually-beneficial {
			color: @blue;
		}

		.images-wrap {
			position: relative;
			width: 100%;
			margin: 0 -24px calc((var(--logo-box-height) / 2) + 14px);
			width: calc(100% + 48px);
			min-height: 250px;

			> * {
				width: 100%;
				text-align: left;
				img {
					width: 100%;
					height: 100%;
					object-fit: cover;
				}
			}

			.lifestyle-image {
				height: 250px;
				overflow: hidden;
			}

			.logo-wrap {
				position: absolute;
				left: 24px;
				bottom: calc(var(--logo-box-height) / -2);
				width: auto;
			}
		}

		.logo {
			background: #fff;
			width: var(--logo-box-width);
			height: var(--logo-box-height);
			padding: 12px;
			box-sizing: border-box;
			overflow: hidden;
			border-radius: @radiusXl;
			.elevation.card();
			display: flex;
			align-items: center;
			justify-content: center;
			img {
				width: auto;
				height: auto;
				max-width: 96%;
				max-height: 100%;
				object-fit: contain;
			}
		}

		.brand-name {
			text-align: left;
			padding: 18px 0 0;
			margin: 0;
			color: @darkContrast;
			font-size: 1.6rem;
			line-height: 1.15;
			a {
				color: inherit;
				text-decoration: none;
			}
		}

		.pitch-your-brand-mobile {
			margin: 30px 0 0;
			display: none;
		}

		.collab-description {
			text-align: left;
			padding: 0;
			margin: 24px 0 0;
			font-size: 1.25rem;
			color: @darkContrast;
			line-height: 1.4;
		}

		.collab-benefits {
			margin: 28px 0 0;
			color: @darkContrast;
			padding: 0;
			text-align: left;

			.benefits-title {
				margin: 0 0 12px;
				color: @darkContrast;
				font-size: 1.125rem;
				line-height: 1.2;
			}

			.benefits-list {
				list-style: none;
				padding: 0;
				margin: 0;
				display: grid;
				grid-template-columns: 1fr;
				gap: 8px 24px;
				li {
					display: flex;
					align-items: center;
					font-size: 1rem;
					line-height: 1.35;
					&:before {
						content: "✓";
						display: inline-block;
						margin-right: 12px;
						width: 20px;
						height: 20px;
						line-height: 22px;
						text-align: center;
						font-size: 0.75rem;
						font-weight: @bold;
						color: #fff;
						background: fade(@formDefaultColour, 85%);
						border-radius: @radiusCircle;
						flex-shrink: 0;
					}
				}
			}
		}

		hr {
			margin: 30px 0 14px;
		}

		.pitch-description {
			position: relative;

			p {
				text-align: left;
				padding: 0;
				margin: 14px 0 5px;
				line-height: 1.7em;
				color: fade(@darkContrast, 90%);

				&.truncated {
					display: -webkit-box;
					line-clamp: 15;
					-webkit-line-clamp: 15;
					-webkit-box-orient: vertical;
					overflow: hidden;
				}

				strong {
					color: @guidedRose;
				}
			}

			.read-more {
				color: @guidedRose;
				cursor: pointer;

				&:hover {
					text-decoration: underline;
				}
			}
		}

		.pitch-button {
			margin: 30px 1em 0 0;
		}

		.already-pitched {
			display: flex;
			align-items: center;
			gap: 10px;
			margin: 22px 0 0;
			color: @textBody;

			.svg {
				width: 18px;
				height: 18px;
				flex: 0 0 auto;
			}

			span {
				font-size: 0.875rem;
				line-height: 1.35;
			}
		}

		.already-pitched-full {
			margin-top: 30px;
		}

		.already-pitched-mini {
			margin-top: 16px;
		}
	}

	&.full {
		margin: 0;
		padding-left: 0;
		padding-right: 0;

		.pitch-to-collab-contact-inner {
			border: 3px solid fade(@formDefaultColour, 50%);
			.rounded(5px);
			padding: 10px;
			margin-top: 30px;
		}

		.pitch-your-brand-mobile {
			display: inline-block;
		}
		.brand-info {
			.category-labels {
				right: 35px;
			}
		}
	}

	&.mini {
		width: 92vw;
		margin: 0 4vw 0 0;
		:deep(.pitch-to-collab-contact) {
			display: none !important;
		}
	}

	&.disabled {
		position: relative;

		.brand-info {
			opacity: 0.2;
			filter: grayscale(100%);
		}

		.overlay {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			z-index: 2;

			> span {
				position: absolute;
				top: 100px;
				left: 50%;
				transform: translate(-50%, 0);
				color: @guidedRose;
				text-align: center;
				width: 380px;
				padding: 20px;
				background: @surfacePanel;
				.rounded(5px);
				.elevation.floatingPanel();

				.button {
					margin-top: 10px;
				}

				.how-it-works {
					font-size: 0.875rem;
					display: block;
					margin-top: 15px;
					cursor: pointer;
					color: @guidedRose;
					&:hover {
						text-decoration: underline;
					}
				}

				p {
					color: @guidedRose;
					margin: 0;
					padding: 10px 0;
				}
			}
		}
	}
}

@media screen and (min-width: 460px) {
	.brand-panel {
		.brand-info {
			.collab-benefits {
				.benefits-list {
					grid-template-columns: 1fr 1fr;
				}
			}
		}
	}
}

@media screen and (min-width: 768px) {
	.brand-panel {
		margin: 20px 0;
		box-sizing: border-box;

		&.mini {
			width: 87.35vw;
			max-width: 100%;

			&.full-menu {
				width: 73vw;
			}
		}

		.brand-info {
			padding: 0 28px;

			.images-wrap {
				margin: 0 -28px calc((var(--logo-box-height) / 2) + 14px);
				width: calc(100% + 56px);
				.lifestyle-image {
					height: 290px;
				}

				.logo-wrap {
					left: 24px;
				}
			}

			.collab-benefits {
				.benefits-list {
					grid-template-columns: 1fr 1fr;
				}
			}

			.brand-name { 
				font-size: 1.75rem;
			}

			.collab-description {
				margin: 24px 0 0;
				font-size: 1.375rem;
			}
		}
	}
}

@media screen and (min-width: 1024px) {
	.brand-panel {
		margin: 20px 20px 20px 0;
		.elevation.floatingPanel();

		.brand-info {
			padding: 0 32px;

			.images-wrap {
				margin: 0 -32px calc((var(--logo-box-height) / 2) + 14px);
				width: calc(100% + 64px);

				.lifestyle-image {
					height: 300px;
				}

				.logo-wrap {
					left: 28px;
				}
			}
		}

		&.mini {
			box-sizing: border-box;
			max-width: 600px;
			margin: 20px;
			.elevation.floatingPanel();
			&.feature {
				margin: 0;
			}
		}
	}
}

@media screen and (min-width: 1200px) and (hover: hover) and (pointer: fine) {
	.brand-panel {
		&.full {
			display: flex;
			justify-content: space-between;

			.brand-info {
				flex-grow: 1;
				.pitch-your-brand-mobile {
					display: none;
				}
			}

			.pitch-to-collab-contact-inner {
				width: 350px;
				flex-shrink: 0;
				flex-grow: 0;
				margin-top: 0;
			}
		}
	}
}

@media screen and (min-width: 1280px) and (hover: hover) and (pointer: fine) {
	.brand-panel {
		&.full {
			.pitch-to-collab-contact-inner {
				padding: 15px;
				width: 400px;
			}
		}
	}
}
</style>
